import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import BlazeAIBrand from "../BlazeAIBrand";
import { ICONS } from "./helpers";

function InventoryArt() {
  // cols: cloud · kind · name · env · region · status · owner
  // Owners cycle through a short roster — realistic (one person owns many
  // resources) and avatar tones are token-driven so dark/light flip cleanly.
  const rows = [
    ["aws",   "ec2",       "ib-test-server",    "prod",  "us-east-1",   "managed",   "Priya S."],
    ["aws",   "rds",       "analytics-db",      "prod",  "us-east-1",   "drifted",   "Marcus T."],
    ["aws",   "s3",        "prod-data-raw",     "prod",  "us-east-1",   "managed",   "Anna W."],
    ["aws",   "alb",       "ingress-main",      "prod",  "us-east-1",   "managed",   "Kenji L."],
    ["aws",   "lambda",    "event-router",      "prod",  "us-east-1",   "ghosted",   "Aisha N."],
    ["aws",   "dynamodb",  "sessions",          "prod",  "us-east-1",   "managed",   "Priya S."],
    ["azure", "vm",        "runner-02",         "stage", "westus-2",    "unmanaged", "Diego R."],
    ["azure", "aks",       "jobs-cluster",      "prod",  "westus-2",    "managed",   "Kenji L."],
    ["gcp",   "gke",       "platform-cluster",  "prod",  "us-central1", "managed",   "Marcus T."],
    ["gcp",   "cloudsql",  "billing-db",        "prod",  "us-central1", "drifted",   "Zoe H."],
    ["gcp",   "gcs",       "ml-artifacts",      "prod",  "us-central1", "managed",   "Omar K."],
    ["aws",   "eks",       "core-services",     "prod",  "us-east-1",   "managed",   "Marcus T."],
  ];

  const ownerTone = (name) => {
    // deterministic tone — same owner always gets the same avatar color
    const palette = ["teal", "amber", "violet", "rose", "green", "teal-dim"];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  };
  const initial = (name) => name.trim().charAt(0).toUpperCase();

  return (
    <div className="surf-svg surf-svg-inventory">
      <div className="inv-head">
        <span className="inv-dot"></span>
        <span className="inv-dot"></span>
        <span className="inv-dot"></span>
        <span className="inv-title">asset inventory · all accounts</span>
      </div>
      <div className="inv-rows">
        {rows.map(([c, k, n, e, r, status, owner], i) => (
          <div className="inv-row" key={i}>
            <span className={`inv-cloud c-${c}`} title={c.toUpperCase()}>
              <i className="inv-cloud-ic" aria-hidden="true" />
            </span>
            <span className="inv-kind">
              <i className={`inv-svc-ic svc-${k}`} aria-hidden="true" />
              <span>{k}</span>
            </span>
            <span className="inv-name">{n}</span>
            <span className={`inv-env env-${e}`}>{e}</span>
            <span className="inv-region">{r}</span>
            <span className={`inv-status st-${status}`}>{status}</span>
            <span className="inv-owner">
              <i className={`inv-owner-av av-${ownerTone(owner)}`} aria-hidden="true">
                {initial(owner)}
              </i>
              <span className="inv-owner-name">{owner}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="inv-foot">
        <span>12,483 assets · 27 accounts · 3 clouds</span>
        <span className="inv-sync">● scanning continuously</span>
      </div>
    </div>
  );
}

function InsightsArt() {
  return (
    <div className="surf-svg surf-svg-insights">
      <div className="ins-query">
        <span className="ins-chev">›</span>
        <span className="ins-text">
          show me every resource touching <b>prod-data</b> that isn't tagged <b>owner=</b>
          <span className="ins-caret"></span>
        </span>
      </div>
      <div className="ins-result">
        <div className="ins-row"><span className="ins-ic">◆</span><span><b>7 resources</b> matched across 2 accounts</span></div>
        <div className="ins-row"><span className="ins-ic">↳</span><span>4 S3 buckets · 2 Lambda fns · 1 IAM role</span></div>
        <div className="ins-row"><span className="ins-ic">·</span><span>grouped by account: <code>data-prod</code> (5), <code>analytics</code> (2)</span></div>
        <div className="ins-row"><span className="ins-ic">·</span><span>last-touched: <code>terraform/data-lake.tf</code> · 3 days ago</span></div>
        <div className="ins-row"><span className="ins-ic">·</span><span>inferred owner from git blame: <code>@platform-data</code></span></div>
        <div className="ins-row"><span className="ins-ic ok">✓</span><span>Drafted PR <code>#491</code> adding <code>owner=data-platform</code></span></div>
        <div className="ins-row"><span className="ins-ic ok">✓</span><span>Policy check passes · 0 new failures introduced</span></div>
      </div>
      <div className="ins-foot">
        <span className="ins-model">claude-sonnet-4</span>
        <span className="ins-tokens">1,284 tok · 640ms</span>
      </div>
    </div>
  );
}

function CostArt() {
  return (
    <div className="surf-svg surf-svg-cost">
      <div className="cost-head">
        <span className="cost-label">FORECAST · NEXT 30 DAYS</span>
        <span className="cost-value">$18,420<span className="cost-unit">/mo</span></span>
        <span className="cost-delta">▲ $412 vs. main</span>
      </div>
      <svg className="cost-chart" viewBox="0 0 320 90" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="costGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#3DD9D0" stopOpacity="0.38" />
            <stop offset="1" stopColor="#3DD9D0" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          className="cost-area"
          d="M0,72 L24,68 L48,70 L72,62 L96,58 L120,60 L144,50 L168,44 L192,48 L216,38 L240,32 L264,28 L288,22 L320,16 L320,90 L0,90 Z"
          fill="url(#costGrad)"
        />
        <path
          className="cost-line"
          d="M0,72 L24,68 L48,70 L72,62 L96,58 L120,60 L144,50 L168,44 L192,48 L216,38 L240,32 L264,28 L288,22 L320,16"
          fill="none"
          stroke="#3DD9D0"
          strokeWidth="1.6"
        />
        <path
          className="cost-line-main"
          d="M0,74 L24,74 L48,75 L72,70 L96,68 L120,70 L144,68 L168,66 L192,66 L216,62 L240,60 L264,58 L288,56 L320,54"
          fill="none"
          stroke="#3A4450"
          strokeWidth="1.2"
          strokeDasharray="3 3"
        />
      </svg>
      <div className="cost-legend">
        <span><i className="cost-swatch teal"></i>this PR</span>
        <span><i className="cost-swatch dim"></i>main</span>
      </div>
      <div className="cost-rows">
        <div className="cost-row"><span>data-lake</span><span className="mono">$6,204</span><span className="cost-bar"><i style={{ width: "62%" }}></i></span></div>
        <div className="cost-row"><span>platform</span><span className="mono">$4,812</span><span className="cost-bar"><i style={{ width: "48%" }}></i></span></div>
        <div className="cost-row"><span>analytics</span><span className="mono">$3,108</span><span className="cost-bar"><i style={{ width: "31%" }}></i></span></div>
        <div className="cost-row"><span>ml-training</span><span className="mono">$2,094</span><span className="cost-bar"><i style={{ width: "21%" }}></i></span></div>
        <div className="cost-row"><span>ingress-nat</span><span className="mono">$1,412</span><span className="cost-bar"><i style={{ width: "14%" }}></i></span></div>
        <div className="cost-row"><span>observability</span><span className="mono">$790</span><span className="cost-bar"><i style={{ width: "8%" }}></i></span></div>
      </div>
    </div>
  );
}

function GovArt() {
  const rows = [
    { state: "ok",   name: "no-public-s3",                  pack: "cis-aws" },
    { state: "ok",   name: "require-owner-tag",             pack: "acme-core" },
    { state: "ok",   name: "encryption-at-rest",            pack: "soc2" },
    { state: "fail", name: "approved-instance-types",       pack: "acme-cost" },
    { state: "ok",   name: "iam-least-privilege",           pack: "cis-aws" },
    { state: "ok",   name: "mfa-on-root",                   pack: "cis-aws" },
    { state: "ok",   name: "kms-key-rotation",              pack: "soc2" },
    { state: "ok",   name: "vpc-flow-logs-enabled",         pack: "cis-aws" },
    { state: "ok",   name: "no-hardcoded-secrets",          pack: "acme-core" },
    { state: "fail", name: "phi-data-residency-us",         pack: "hipaa" },
    { state: "ok",   name: "prod-approver-required",        pack: "acme-core" },
    { state: "ok",   name: "no-wildcard-iam-actions",       pack: "cis-aws" },
  ];
  const passCount = rows.filter((r) => r.state === "ok").length;
  return (
    <div className="surf-svg surf-svg-gov">
      <div className="gov-head">
        <span className="gov-label">POLICIES · platform/refactor</span>
        <span className="gov-score"><b>{passCount}</b> / {rows.length}</span>
      </div>
      <div className="gov-rows">
        {rows.map((r, i) => (
          <div className={`gov-row ${r.state}`} key={i}>
            <span className="gov-ic">{r.state === "ok" ? "✓" : "✕"}</span>
            <span className="gov-name">{r.name}</span>
            <span className="gov-pack">{r.pack}</span>
          </div>
        ))}
      </div>
      <div className="gov-foot">
        <span>Evidence attached to PR <code>#482</code></span>
      </div>
    </div>
  );
}

const TILES = [
  {
    num: "01",
    title: "Inventory",
    blurb: "AWS, Azure, GCP, and Kubernetes — one live inventory, continuously scanned.",
    detail: {
      eyebrow: "INVENTORY",
      h: "Every resource, every account, every cloud.",
      lead: "Unify your cloud inventory across multiple clouds, accounts, and SaaS tools (Bitbucket, Git, Okta, and more) in a single view. Monitor asset history and health at a glance.",
      bullets: [
        "Auto-discovery across AWS, Azure, GCP and Kubernetes",
        "Live status: managed, drifted, unmanaged, ghosted",
        "Searchable inventory — slice by account, env, team, or tag",
      ],
      Art: InventoryArt,
    },
  },
  {
    num: "02",
    title: "Insights",
    blurb: (<>Ask <BlazeAIBrand /> anything about your cloud — in plain English.</>),
    detail: {
      eyebrow: (<>INSIGHTS · <BlazeAIBrand /></>),
      h: "Stop querying your cloud. Start asking it.",
      lead: (<><BlazeAIBrand />'s subagents — cost, naming, inventory, policy, insights — are woven into the fabric of your cloud. Ask what you actually want to know: "Where am I wasting money?" "What's drifted since Friday?" "Who's breaking naming conventions?" Get answers, not dashboards.</>),
      bullets: [
        "Specialized engines woven into your cloud: cost, naming, inventory, policy, insights",
        "Direct answers — spend, idle resources, drift, policy violations",
        "Claude · GPT · Bedrock — or BYO LLM, data stays in-cluster",
      ],
      Art: InsightsArt,
    },
  },
  {
    num: "03",
    title: "Cost Center",
    blurb: "Cost that maps to code, branches, and teams — not invoices.",
    detail: {
      eyebrow: "COST CENTER",
      h: "Cost that maps to code, not invoices.",
      lead: "Projected, direct, and implied cost — attached to every resource, rolled up to every stack, every team, every branch. See forecasts change with every PR.",
      bullets: [
        "Implied costs surfaced (cross-AZ, NAT, data transfer)",
        "Forecast delta attached to every pull request",
        "Commitment-plan aware (Reserved, Savings Plans, CUDs)",
      ],
      Art: CostArt,
    },
  },
  {
    num: "04",
    title: "Governance",
    blurb: "Policy-as-code — evaluated on the design and on the live cloud.",
    detail: {
      eyebrow: "GOVERNANCE",
      h: "Policies that run on both sides.",
      lead: "OPA rules evaluate against new design before any plan runs, and against live inventory continuously. Evidence attaches to every PR, and policy drift surfaces the moment it appears.",
      bullets: [
        "OPA / Rego native — BYO policies or use the packs",
        "SOC 2 + CIS + HIPAA out-of-the-box policies",
        "Pass/fail evidence auto-attached to every merge",
      ],
      Art: GovArt,
    },
  },
];

export default function Platform() {
  const [active, setActive] = useState(0);
  const detail = TILES[active].detail;
  const Art = detail.Art;

  return (
    <section id="platform" className="platform">
      <div className="wrap">
        <div className="platform-head reveal">
          <span className="eyebrow">The full toolkit</span>
          <h2 className="h1">
            Built into the platform. <span style={{ color: "var(--ink-6)" }}>Not bolted on.</span>
          </h2>
          <p className="lead" style={{ marginTop: 16 }}>
            Inventory, insights, cost, and governance run continuously against your live cloud. The Workbench turns new design into reviewable IaC. One platform keeping both sides in sync.
          </p>
        </div>

        <div className="reveal">
          <div className="flow tk-flow">
            {TILES.map((t, i) => (
              <div
                key={t.num}
                className={"flow-step" + (i === active ? " is-active" : "")}
                onClick={() => setActive(i)}
              >
                <span className="num">{t.num} · {t.title}</span>
                <h4>{t.title}</h4>
                <p>{t.blurb}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flow-detail tk-detail reveal">
          <div className="flow-detail-copy">
            <span className="eyebrow">{detail.eyebrow}</span>
            <h3 className="h2" style={{ fontSize: 28 }}>{detail.h}</h3>
            <p className="lead" style={{ fontSize: 16 }}>{detail.lead}</p>
            <ul>
              {detail.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <RouterLink to="/docs" className="btn-link">
              Read the docs
              <span dangerouslySetInnerHTML={{ __html: `<svg class="ic-svg" viewBox="0 0 24 24" width="14" height="14"><path d="${ICONS.arrow}"/></svg>` }} />
            </RouterLink>
          </div>
          <div className="flow-detail-img tk-detail-img">
            <Art />
          </div>
        </div>
      </div>
    </section>
  );
}
